/**
 * Public Scholar-Athlete Digital Portfolio — shareable, verified page for charter
 * auditors and college recruiters. Read-only, no auth. Route: /academy/portfolio/:slug
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, Award, Flame, ExternalLink, GraduationCap, Trophy, Star } from 'lucide-react';
import api from '../services/api';

const ScholarPortfolio = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | private

  useEffect(() => {
    api.get(`/academy/portfolio/public/${slug}`)
      .then((r) => { setP(r.data.portfolio); setState('ok'); })
      .catch(() => setState('private'));
  }, [slug]);

  if (state === 'loading') return (<div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>);
  if (state === 'private') return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-6" data-testid="portfolio-private">
      <ShieldCheck className="w-10 h-10 text-zinc-700" />
      <h1 className="text-xl font-black text-white mt-4">This portfolio is private</h1>
      <p className="text-sm text-zinc-500 mt-1">The scholar has not made this portfolio public.</p>
    </div>
  );

  const initials = (p.name || 'S').split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#050508' }} data-testid="public-portfolio">
      {/* Header */}
      <div className="relative border-b-2 border-zinc-900 px-6 py-14" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(245,179,1,1) 1px, transparent 1px), linear-gradient(90deg, rgba(245,179,1,1) 1px, transparent 1px)', backgroundSize: '54px 54px' }} />
        <div className="relative max-w-5xl mx-auto flex flex-wrap items-center gap-6">
          <div className="w-24 h-24 rounded-full flex items-center justify-center border-2 border-yellow-500 overflow-hidden flex-shrink-0" style={{ background: '#0a1636' }}>
            {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl font-black text-yellow-400">{initials}</span>}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-yellow-500"><GraduationCap className="w-3.5 h-3.5" /> Scholar Athlete Academy</div>
            <h1 className="text-3xl md:text-4xl font-black mt-1" data-testid="portfolio-name">{p.name}</h1>
            <div className="text-sm text-zinc-400 mt-1">Grade {p.grade_level}{p.sport ? ` · ${p.sport}` : ''}{p.position ? ` · ${p.position}` : ''}{p.city ? ` · ${p.city}` : ''}</div>
          </div>
          <div className="flex gap-6">
            <Stat label="Scholar Rating" value={p.overall_rating} accent="#F5B301" />
            <Stat label="Level" value={p.level} />
            <Stat label="Day Streak" value={p.streak_days} icon={Flame} />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-14">
        {/* Verified Capstones */}
        <section data-testid="portfolio-capstones">
          <SectionHead icon={ShieldCheck} title="Verified Capstone Submissions" sub="Applied, evidence-based projects completed and verified by the Academy." />
          {p.capstones?.length ? (
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {p.capstones.map((c, i) => (
                <div key={i} className="border-2 border-zinc-800 bg-zinc-950 p-5" data-testid={`portfolio-capstone-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-black text-white">{c.course_title}</div>
                      {c.badge_name && <div className="text-[11px] text-yellow-400 font-bold mt-0.5">{c.badge_name}</div>}
                    </div>
                    <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-1.5 py-1 whitespace-nowrap"><ShieldCheck className="w-3 h-3" /> Verified</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-zinc-500">
                    {typeof c.ai_score === 'number' && <span>Score: <span className="text-white font-bold">{c.ai_score}%</span></span>}
                    {c.credit && <span>Credit: <span className="text-white font-bold">+{c.credit.amount} {c.credit.type}</span></span>}
                    {c.completed_at && <span>{String(c.completed_at).slice(0, 10)}</span>}
                  </div>
                  {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-yellow-400 hover:text-yellow-300"><ExternalLink className="w-3.5 h-3.5" /> View Submission</a>}
                </div>
              ))}
            </div>
          ) : (<p className="text-sm text-zinc-600 mt-4">No verified capstones yet.</p>)}
        </section>

        {/* CA Standard Mastery */}
        <section data-testid="portfolio-mastery">
          <SectionHead icon={Star} title="CA Standard Mastery Metrics" sub="Standards-aligned mastery across core subjects, sports analytics, and consistency." />
          <div className="mt-6 space-y-3">
            {p.mastery?.vertices?.map((v) => (
              <div key={v.key} data-testid={`mastery-${v.key}`}>
                <div className="flex justify-between text-[11px] mb-1"><span className="text-zinc-400 font-bold">{v.label}</span><span className="text-white font-black">{v.score}%</span></div>
                <div className="h-2 bg-zinc-900"><div className="h-full" style={{ width: `${v.score}%`, backgroundColor: '#F5B301' }} /></div>
              </div>
            ))}
          </div>
        </section>

        {/* Badges & Credits */}
        <section data-testid="portfolio-badges">
          <SectionHead icon={Trophy} title="Badges & Elective Credits" sub={`${p.total_elective_credits || 0} elective credits earned toward a well-rounded scholar.`} />
          <div className="flex flex-wrap gap-2 mt-6">
            {[...(p.elective_badges || []), ...(p.gamification_badges || [])].map((b, i) => (
              <span key={i} className="flex items-center gap-1.5 border-2 border-yellow-500/40 bg-yellow-500/10 px-3 py-1.5" data-testid={`portfolio-badge-${i}`}>
                <Award className="w-3.5 h-3.5 text-yellow-400" /><span className="text-[10px] font-black uppercase tracking-widest text-yellow-300">{b.name}</span>
              </span>
            ))}
            {![...(p.elective_badges || []), ...(p.gamification_badges || [])].length && <span className="text-sm text-zinc-600">No badges earned yet.</span>}
          </div>
        </section>
      </div>

      <div className="border-t-2 border-zinc-900 px-6 py-8 text-center">
        <div className="flex items-center justify-center gap-2 text-[11px] text-zinc-500"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Verified academic ledger · PRSVR Scholar Athlete Academy · California State Standards Aligned</div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent = '#ffffff', icon: Icon }) => (
  <div className="text-center">
    <div className="text-3xl font-black flex items-center gap-1 justify-center" style={{ color: accent }}>{Icon && <Icon className="w-5 h-5" />}{value}</div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
  </div>
);

const SectionHead = ({ icon: Icon, title, sub }) => (
  <div>
    <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-yellow-500" /><h2 className="text-lg md:text-xl font-black uppercase tracking-tight">{title}</h2></div>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

export default ScholarPortfolio;
